import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { motion } from 'framer-motion';

interface CommitteePersona {
  persona_id: string;
  role: string;
  status: 'waiting' | 'active' | 'completed';
  start_time?: string;
  end_time?: string;
}

interface CommitteeExecutionVisualizerProps {
  committeeMode: string;
  personas: CommitteePersona[];
  currentPersonaIndex?: number;
  iteration?: number;
  totalIterations?: number;
  phase?: string;
}

export const CommitteeExecutionVisualizer: React.FC<CommitteeExecutionVisualizerProps> = ({
  committeeMode,
  personas,
  currentPersonaIndex = -1,
  iteration = 1,
  totalIterations = 1,
  phase
}) => {
  const { darkMode } = useTheme();
  
  // Helper function to get status color
  const getStatusColor = (status: string) => {
    switch(status) {
      case 'waiting': return darkMode ? 'bg-gray-700' : 'bg-gray-200';
      case 'active': return 'bg-blue-500';
      case 'completed': return 'bg-green-500';
      default: return darkMode ? 'bg-gray-700' : 'bg-gray-200';
    }
  };
  
  // Render different visualizations based on committee mode
  const renderVisualization = () => {
    switch(committeeMode) {
      case 'sequential':
        return renderSequentialVisualization(phase || 'initial');
      case 'parallel':
        return renderParallelVisualization();
      case 'iterative':
        return renderIterativeVisualization();
      case 'collaborative':
        return renderCollaborativeVisualization();
      case 'debate':
        return renderDebateVisualization();
      default:
        return renderSequentialVisualization(phase);
    }
  };
  
  // Sequential mode visualization
  const renderSequentialVisualization = (currentPhase?: string) => (
    <div className="sequential-visualization">
      <div className="flex items-center mb-4">
        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Sequential Execution: Each persona builds on previous contributions
        </div>
      </div>
      
      <div className="relative">
        {/* Connector line */}
        <div className={`absolute left-1/2 top-0 bottom-0 w-0.5 transform -translate-x-1/2 ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
        
        {/* Personas */}
        <div className="space-y-6">
          {personas.map((persona, index) => (
            <motion.div 
              key={persona.persona_id} 
              className="relative"
              initial={index === currentPersonaIndex ? { scale: 0.95 } : { scale: 1 }}
              animate={index === currentPersonaIndex 
                ? { scale: 1.03, transition: { yoyo: Infinity, duration: 1.5 } }
                : { scale: 1 }
              }
            >
              {/* Connector dot with animation for active persona */}
              <motion.div 
                className={`absolute left-1/2 top-1/2 w-3 h-3 rounded-full transform -translate-x-1/2 -translate-y-1/2 ${getStatusColor(persona.status)}`}
                animate={persona.status === 'active' ? {
                  scale: [1, 1.3, 1],
                  transition: { repeat: Infinity, duration: 1.5 }
                } : {}}
              />
              
              <div className={`flex items-center justify-between p-3 rounded-lg ${
                index === currentPersonaIndex 
                  ? darkMode ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200'
                  : darkMode ? 'bg-gray-800' : 'bg-white'
              } ${
                persona.status === 'completed' 
                  ? darkMode ? 'border-green-700' : 'border-green-200'
                  : darkMode ? 'border-gray-700' : 'border-gray-200'
              } border`}>
                <div className="flex-1 ml-4">
                  <div className="font-medium">{persona.persona_id}</div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Role: {persona.role}</div>
                  
                  {/* Add building indicator for sequential mode */}
                  {index > 0 && (
                    <div className={`text-xs mt-1 ${
                      darkMode ? 'text-blue-400' : 'text-blue-600'
                    }`}>
                      Building on {index} previous contribution{index > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
                
                <div className={`px-2 py-1 text-xs rounded ${
                  persona.status === 'waiting' 
                    ? darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                    : persona.status === 'active'
                      ? 'bg-blue-500 text-white'
                      : 'bg-green-500 text-white'
                }`}>
                  {persona.status === 'waiting' ? 'Waiting' : 
                   persona.status === 'active' ? 'Active' : 'Completed'}
                </div>
              </div>
              
              {/* Add a typing indicator for active personas */}
              {persona.status === 'active' && (
                <div className="mt-2 flex space-x-1 justify-center">
                  <motion.div 
                    className="w-2 h-2 bg-blue-500 rounded-full"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 0.6 }}
                  />
                  <motion.div 
                    className="w-2 h-2 bg-blue-500 rounded-full"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }}
                  />
                  <motion.div 
                    className="w-2 h-2 bg-blue-500 rounded-full"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }}
                  />
                </div>
              )}
              
              {/* Add building arrows between personas */}
              {index < personas.length - 1 && (
                <div className={`absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-3 text-lg ${
                  darkMode ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  ↓
                  {persona.status === 'completed' && (
                    <span className={`absolute left-1/2 transform -translate-x-1/2 translate-y-1 text-xs whitespace-nowrap ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Builds on
                    </span>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
      
      {/* Add review phase */}
      {currentPhase === 'review' && (
        <div className="mt-4">
          <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Review Phase: Reviewer is evaluating committee outputs
          </div>
          
          <div className="mt-2 p-3 rounded-lg border bg-purple-100 border-purple-300 dark:bg-purple-900 dark:border-purple-700">
            <div className="font-medium">Review in Progress</div>
            <div className="text-sm mt-1">
              The reviewer is analyzing and critiquing the committee's work
            </div>
          </div>
        </div>
      )}
      
      {/* Add review phase */}
      {currentPhase === 'review' && (
        <div className="mt-4">
          <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Review Phase: Reviewer is evaluating committee outputs
          </div>
          
          <div className="mt-2 p-3 rounded-lg border bg-purple-100 border-purple-300 dark:bg-purple-900 dark:border-purple-700">
            <div className="font-medium">Review in Progress</div>
            <div className="text-sm mt-1">
              The reviewer is analyzing and critiquing the committee's work
            </div>
          </div>
        </div>
      )}
    </div>
  );
  
  // Parallel mode visualization
  const renderParallelVisualization = () => (
    <div className="parallel-visualization">
      <div className="flex items-center mb-4">
        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Parallel Execution: All personas work simultaneously
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {personas.map((persona) => (
          <div 
            key={persona.persona_id} 
            className={`p-3 rounded-lg border ${
              persona.status === 'active' 
                ? darkMode ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200'
                : persona.status === 'completed'
                  ? darkMode ? 'bg-green-900 border-green-700' : 'bg-green-50 border-green-200'
                  : darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium">{persona.persona_id}</div>
              <div className={`w-2 h-2 rounded-full ${getStatusColor(persona.status)}`}></div>
            </div>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Role: {persona.role}</div>
            
            {persona.start_time && (
              <div className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Started: {new Date(persona.start_time).toLocaleTimeString()}
              </div>
            )}
            
            {persona.end_time && (
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Completed: {new Date(persona.end_time).toLocaleTimeString()}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
  
  // Iterative mode visualization
  const renderIterativeVisualization = () => (
    <div className="iterative-visualization">
      <div className="flex items-center justify-between mb-4">
        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Iterative Execution: Multiple rounds of sequential execution
        </div>
        
        <div className={`px-3 py-1 text-sm rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
          Iteration {iteration} of {totalIterations}
        </div>
      </div>
      
      {/* Render sequential visualization for current iteration */}
      {renderSequentialVisualization()}
    </div>
  );
  
  // Collaborative mode visualization
  const renderCollaborativeVisualization = () => (
    <div className="collaborative-visualization">
      <div className="flex items-center mb-4">
        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Collaborative Execution: Personas work with a shared workspace
        </div>
      </div>
      
      <div className="flex flex-col md:flex-row gap-4">
        {/* Personas */}
        <div className="md:w-1/3 space-y-3">
          {personas.map((persona) => (
            <div 
              key={persona.persona_id} 
              className={`p-3 rounded-lg border ${
                persona.status === 'active' 
                  ? darkMode ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200'
                  : persona.status === 'completed'
                    ? darkMode ? 'bg-green-900 border-green-700' : 'bg-green-50 border-green-200'
                    : darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="font-medium">{persona.persona_id}</div>
                <div className={`w-2 h-2 rounded-full ${getStatusColor(persona.status)}`}></div>
              </div>
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Role: {persona.role}</div>
            </div>
          ))}
        </div>
        
        {/* Shared workspace visualization */}
        <div className={`md:w-2/3 p-4 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="font-medium mb-2">Shared Workspace</div>
          <div className={`p-3 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} min-h-[200px]`}>
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Personas are collaborating in real-time...
            </div>
            
            {/* This would be populated with actual shared workspace content */}
          </div>
        </div>
      </div>
    </div>
  );
  
  // Debate mode visualization
  const renderDebateVisualization = () => (
    <div className="debate-visualization">
      <div className="flex items-center mb-4">
        <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Debate Execution: Personas propose, critique, and respond
        </div>
      </div>
      
      <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="font-medium mb-3">Debate Progress</div>
        
        <div className="space-y-4">
          <div className={`p-3 rounded ${
            currentPersonaIndex === 0 ? 'bg-blue-500 text-white' : darkMode ? 'bg-gray-700' : 'bg-gray-100'
          }`}>
            <div className="font-medium">Phase 1: Initial Proposals</div>
            <div className="text-sm mt-1">Each persona proposes an initial solution</div>
          </div>
          
          <div className={`p-3 rounded ${
            currentPersonaIndex === 1 ? 'bg-blue-500 text-white' : darkMode ? 'bg-gray-700' : 'bg-gray-100'
          }`}>
            <div className="font-medium">Phase 2: Critiques</div>
            <div className="text-sm mt-1">Personas critique each other's proposals</div>
          </div>
          
          <div className={`p-3 rounded ${
            currentPersonaIndex === 2 ? 'bg-blue-500 text-white' : darkMode ? 'bg-gray-700' : 'bg-gray-100'
          }`}>
            <div className="font-medium">Phase 3: Responses</div>
            <div className="text-sm mt-1">Personas respond to critiques of their proposals</div>
          </div>
          
          <div className={`p-3 rounded ${
            currentPersonaIndex === 3 ? 'bg-blue-500 text-white' : darkMode ? 'bg-gray-700' : 'bg-gray-100'
          }`}>
            <div className="font-medium">Phase 4: Final Synthesis</div>
            <div className="text-sm mt-1">A designated persona creates the final synthesis</div>
          </div>
        </div>
      </div>
    </div>
  );
  
  return (
    <div className="committee-execution-visualizer">
      <h3 className={`text-lg font-medium mb-4 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
        Committee Execution Progress
      </h3>
      
      {renderVisualization()}
    </div>
  );
};
